import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import vaultdownloadsService from '../services/vaultdownloadsService';
import vaultService from '../services/vaultService';
import hotelService from '../services/hotelService';
import accountService from '../services/accountService';
import './VaultDownloadsList.css';

const PLATFORMS = {
  'windows-x86_64': 'Windows (x86_64)',
  'linux-x86_64': 'Linux (x86_64)',
  'linux-arm64': 'Linux (ARM64)',
  'darwin-amd64': 'macOS (Intel)',
  'darwin-arm64': 'macOS (Apple Silicon)'
};

const PLATFORM_INSTRUCTIONS = {
  'windows-x86_64': {
    icon: '🪟',
    steps: [
      'Download the .exe file using the button above.',
      'Open Command Prompt or PowerShell and navigate to your downloads folder.',
      (filename) => <>Run the app: <code className="inline-code">{filename}</code></>,
      <>If Windows SmartScreen blocks it, click <strong>More info</strong> then <strong>Run anyway</strong>.</>
    ]
  },
  'linux-x86_64': {
    icon: '🐧',
    steps: [
      'Download the file using the button above.',
      'Open a terminal and navigate to your downloads folder.',
      (filename) => <>Make it executable: <code className="inline-code">chmod +x {filename}</code></>,
      (filename) => <>Run the app: <code className="inline-code">./{filename}</code></>
    ]
  },
  'linux-arm64': {
    icon: '🐧',
    steps: [
      'Download the file using the button above.',
      'Open a terminal and navigate to your downloads folder.',
      (filename) => <>Make it executable: <code className="inline-code">chmod +x {filename}</code></>,
      (filename) => <>Run the app: <code className="inline-code">./{filename}</code></>
    ]
  },
  'darwin-amd64': {
    icon: '🍎',
    steps: [
      'Download the file using the button above.',
      'Open Terminal and navigate to your downloads folder.',
      (filename) => <>Make it executable: <code className="inline-code">chmod +x {filename}</code></>,
      (filename) => <>Run the app: <code className="inline-code">./{filename}</code></>,
      <>If macOS blocks the app, go to <strong>System Settings → Privacy &amp; Security</strong> and click <strong>Allow Anyway</strong>, then run again.</>
    ]
  },
  'darwin-arm64': {
    icon: '🍎',
    steps: [
      'Download the file using the button above.',
      'Open Terminal and navigate to your downloads folder.',
      (filename) => <>Make it executable: <code className="inline-code">chmod +x {filename}</code></>,
      (filename) => <>Run the app: <code className="inline-code">./{filename}</code></>,
      <>If macOS blocks the app, go to <strong>System Settings → Privacy &amp; Security</strong> and click <strong>Allow Anyway</strong>, then run again.</>
    ]
  }
};

const VaultDownloadsList = () => {
  const navigate = useNavigate();
  const { vaultid } = useParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [vaultName, setVaultName] = useState('');
  const [hotelName, setHotelName] = useState('');
  const [accountName, setAccountName] = useState('');
  const [accountId, setAccountId] = useState('');
  const [hotelId, setHotelId] = useState('');
  const [selectedPlatform, setSelectedPlatform] = useState('windows-x86_64');
  const [generating, setGenerating] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState(null);
  const [pollTimer, setPollTimer] = useState(null);

  useEffect(() => {
    if (vaultid) {
      fetchVault();
    }

    // Cleanup poll timer on unmount
    return () => {
      if (pollTimer) {
        clearInterval(pollTimer);
      }
    };
  }, [vaultid]);

  const fetchVault = async () => {
    try {
      const response = await vaultService.getVaultById(vaultid);
      const vaultData = response.data;
      setVaultName(vaultData.name || '');
      setHotelId(vaultData.hotelid);

      if (vaultData.hotelid) {
        fetchHotel(vaultData.hotelid);
      }
    } catch (err) {
      console.error('Failed to fetch vault:', err);
    }
  };

  const fetchHotel = async (hotelId) => {
    try {
      const response = await hotelService.getHotelById(hotelId);
      const hotelData = response.data.hotel || response.data;
      setHotelName(hotelData.hotelname || '');
      setAccountId(hotelData.accountid);

      if (hotelData.accountid) {
        fetchAccount(hotelData.accountid);
      }
    } catch (err) {
      console.error('Failed to fetch hotel:', err);
    }
  };

  const fetchAccount = async (accId) => {
    try {
      const response = await accountService.getAccountById(accId);
      const accountData = response.data.account || response.data;
      setAccountName(accountData.COMPANYNAME || '');
    } catch (err) {
      console.error('Failed to fetch account:', err);
    }
  };

  const startPolling = (downloadId) => {
    let pollCount = 0;
    const maxPolls = 60; // 5 minutes at 5 second intervals
    const pollInterval = 5000; // 5 seconds

    // Poll immediately
    checkDownloadStatus(downloadId);

    // Set up interval polling
    const intervalId = setInterval(async () => {
      pollCount++;

      if (pollCount >= maxPolls) {
        // Stop polling after 5 minutes
        clearInterval(intervalId);
        setPollTimer(null);
        setGenerating(false);
        setError('App generation timed out after 5 minutes. Please try again.');
        return;
      }

      await checkDownloadStatus(downloadId);
    }, pollInterval);

    setPollTimer(intervalId);
  };

  const checkDownloadStatus = async (downloadId) => {
    try {
      const response = await vaultdownloadsService.getVaultDownloadById(downloadId);
      const download = response.data;

      if (download.status === 'completed') {
        // Stop polling
        if (pollTimer) {
          clearInterval(pollTimer);
          setPollTimer(null);
        }

        setGenerating(false);
        setDownloadUrl(download.s3url);
        setSuccess(`App generated successfully for ${PLATFORMS[selectedPlatform]}!`);
      } else if (download.status === 'failed') {
        // Stop polling
        if (pollTimer) {
          clearInterval(pollTimer);
          setPollTimer(null);
        }

        setGenerating(false);
        setError(`Failed to generate ${PLATFORMS[selectedPlatform]} app. Please try again.`);
      }
    } catch (err) {
      console.error('Error checking download status:', err);
    }
  };

  const handleGenerate = async (e) => {
    e.preventDefault();

    try {
      setError(null);
      setSuccess(null);
      setDownloadUrl(null);
      setGenerating(true);

      const response = await vaultdownloadsService.generateVaultDownload(vaultid, selectedPlatform);
      const downloadId = response.data.download?.id;

      if (downloadId) {
        // Start polling for status updates
        startPolling(downloadId);
      }
    } catch (err) {
      setGenerating(false);
      setError(err.response?.data?.error?.message || 'Failed to start app generation');
    }
  };

  const handleDownload = () => {
    if (downloadUrl) {
      window.open(downloadUrl, '_blank');
    }
  };

  const getFilename = (url) => {
    if (!url) return '';
    try {
      const path = new URL(url).pathname;
      return path.split('/').pop();
    } catch {
      return url.split('/').pop().split('?')[0];
    }
  };

  return (
    <div className="vaultdownloads-container">
      {/* Breadcrumb Hierarchy */}
      {(accountName || hotelName || vaultName) && (
        <div className="breadcrumb">
          {accountName && accountId && (
            <>
              <Link to={`/accounts/${accountId}`} className="breadcrumb-link">
                {accountName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {hotelName && hotelId && (
            <>
              <Link to={`/hotel/${hotelId}`} className="breadcrumb-link">
                {hotelName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          {vaultName && vaultid && (
            <>
              <Link to={`/vault/${vaultid}`} className="breadcrumb-link">
                {vaultName}
              </Link>
              <span className="breadcrumb-separator">/</span>
            </>
          )}
          <span className="breadcrumb-current">Generate Client</span>
        </div>
      )}

      <div className="vaultdownloads-header">
        <h1>Generate Vault Client</h1>
        <button
          className="btn-secondary"
          onClick={() => navigate(`/vault/${vaultid}`)}
        >
          ← Back to Vault
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}
      {success && <div className="success-message">{success}</div>}

      <div className="generate-form-container">
        <form onSubmit={handleGenerate} className="generate-form">
          <div className="form-group">
            <label htmlFor="platform">Select Platform:</label>
            <select
              id="platform"
              value={selectedPlatform}
              onChange={(e) => setSelectedPlatform(e.target.value)}
              disabled={generating}
              className="platform-select"
            >
              {Object.entries(PLATFORMS).map(([key, label]) => (
                <option key={key} value={key}>
                  {label}
                </option>
              ))}
            </select>
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn-generate"
              disabled={generating}
            >
              {generating ? (
                <>
                  <span className="spinner"></span>
                  Generating...
                </>
              ) : (
                'Generate App'
              )}
            </button>

            {downloadUrl && (
              <button
                type="button"
                className="btn-download-large"
                onClick={handleDownload}
              >
                ⬇️ Download App
              </button>
            )}
          </div>
        </form>

        {generating && (
          <div className="generating-status">
            <div className="spinner-large"></div>
            <p>Generating app for {PLATFORMS[selectedPlatform]}...</p>
            <p className="status-note">This may take up to 5 minutes. Please wait...</p>
          </div>
        )}

        {downloadUrl && (() => {
          const instructions = PLATFORM_INSTRUCTIONS[selectedPlatform];
          const filename = getFilename(downloadUrl);
          return (
            <div className="run-instructions">
              <h3>{instructions.icon} How to run on {PLATFORMS[selectedPlatform]}</h3>
              <ol className="instructions-list">
                {instructions.steps.map((step, i) => (
                  <li key={i}>{typeof step === 'function' ? step(filename) : step}</li>
                ))}
              </ol>
            </div>
          );
        })()}
      </div>
    </div>
  );
};

export default VaultDownloadsList;
